import {
    ChecklistItemType,
    type ChecklistToc as ChecklistTocType,
    type Status,
} from "../types";
import classes from "./checklist-toc.module.css";
import {CircularProgress} from "./CircularProgress";
import InfoCircleIcon from "../../../assets/v2-info-circle.svg?react";
import {useChecklistData} from "./ChecklistDataContext";

type TocItemLinkProps = {
    id: string;
    title: string;
    onClick: () => void;
    isInfo: boolean;
    progress?: {
        value: number;
        max: number;
    };
};

const TocItemLink = ({
    id,
    title,
    onClick,
    isInfo,
    progress,
}: TocItemLinkProps) => (
    <div className={classes.tocItem}>
        {isInfo && (
            <InfoCircleIcon
                className={classes.infoIcon}
                width={18}
                height={18}
            />
        )}
        {progress && (
            <CircularProgress
                className={classes.circularProgress}
                value={progress.value}
                max={progress.max}
            />
        )}
        <a href={`#${id}`} className={classes.tocLink} onClick={(e) => { e.preventDefault(); onClick(); }}>
            <span>{title}</span>
        </a>
    </div>
);

/**
 * Calculate progress for a node by traversing flat toc using IDs.
 * Uses getStatus from context to get pre-computed status values.
 */
const calculateNodeProgress = (
    nodeId: string,
    viewFilteredToc: ChecklistTocType,
    getStatus: (id: string) => Status | -1,
): {value: number; max: number} | undefined => {
    const node = viewFilteredToc[nodeId];
    if (!node?.children || node.children.length === 0) {
        return undefined;
    }

    let value = 0;
    let max = 0;

    const traverse = (id: string) => {
        const current = viewFilteredToc[id];
        if (!current) return;

        const status = getStatus(id);

        // Leaf node that's not an info node (info nodes have status -1)
        if (
            current.checkItemType === ChecklistItemType.CheckItem &&
            status !== -1
        ) {
            max += 1;
            if (status !== 0) {
                // Status.todo === 0
                value += 1;
            }
            return;
        }

        // Recurse into children
        current.children?.forEach(traverse);
    };

    traverse(nodeId);

    return max > 0 ? {value, max} : undefined;
};

/**
 * TOC tree component. Always renders all levels — CSS handles
 * visibility via :has([data-toc-active]) for ancestor expansion
 * and li[data-toc-active] > .tocList for child expansion.
 * The data-toc-active attribute is toggled imperatively by the
 * scroll spy in ChecklistResource.
 */
const ChecklistToc = ({
    nodeId,
    isRoot = false,
}: {
    nodeId: string;
    isRoot?: boolean;
}) => {
    const {viewFilteredToc, isHidden, getStatus, isDraft, onClickTocItem} =
        useChecklistData();

    const node = viewFilteredToc[nodeId];
    if (!node) {
        return null;
    }

    // Filter children to only include CheckItemParent nodes
    const childIds = (node.children || []).filter((childId) => {
        const child = viewFilteredToc[childId];
        return child?.checkItemType === ChecklistItemType.CheckItemParent;
    });

    if (childIds.length === 0) {
        return null;
    }

    const body = childIds
        .filter((childId) => !isHidden(childId))
        .map((childId) => {
            const child = viewFilteredToc[childId];
            if (!child) return null;

            const isInfoNode = getStatus(childId) === -1;

            const progress =
                !isRoot || isInfoNode || isDraft
                    ? undefined
                    : calculateNodeProgress(
                          childId,
                          viewFilteredToc,
                          getStatus,
                      );

            return (
                <li
                    key={childId}
                    data-id={`toc-item-${childId}`}
                >
                    <TocItemLink
                        id={childId}
                        title={child.title ?? ""}
                        onClick={() => onClickTocItem(childId, nodeId)}
                        isInfo={isInfoNode}
                        progress={progress}
                    />
                    <ChecklistToc nodeId={childId} />
                </li>
            );
        });

    return (
        <ul
            key={nodeId}
            className={`${classes.tocList} ${
                isRoot ? classes.tocListRoot : ""
            } ${isDraft ? classes.tocListDraft : ""}`}
        >
            {body}
        </ul>
    );
};

export default ChecklistToc;
